class TrustIndicators extends HTMLElement {
  #mediaQuery = window.matchMedia('(max-width: 749px)');
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #timer;
  #cycleResetTimer;
  #clone;
  #index = 0;
  #dots = [];

  connectedCallback() {
    this.#mediaQuery.addEventListener('change', this.#update);
    this.#reducedMotionQuery.addEventListener('change', this.#update);
    this.addEventListener('transitionend', this.#handleTransitionEnd);
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    this.#buildPagination();
    this.#update();
  }

  disconnectedCallback() {
    this.#mediaQuery.removeEventListener('change', this.#update);
    this.#reducedMotionQuery.removeEventListener('change', this.#update);
    this.removeEventListener('transitionend', this.#handleTransitionEnd);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    this.#stop();
  }

  get #track() {
    return this.querySelector('.trust-indicators__track');
  }

  get #items() {
    return [...(this.#track?.children ?? [])].filter((item) => item !== this.#clone);
  }

  #buildPagination() {
    const pagination = this.querySelector('.trust-indicators__pagination');
    if (!pagination) return;
    pagination.replaceChildren();
    this.#dots = this.#items.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'trust-indicators__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Show trust benefit ${index + 1}`);
      dot.addEventListener('click', () => this.#goTo(index));
      pagination.append(dot);
      return dot;
    });
    this.#setActiveDot();
  }

  #update = () => {
    if (this.#mediaQuery.matches) {
      this.#start();
    } else {
      this.#stop();
    }
  };

  #start() {
    const track = this.#track;
    if (!track || this.#clone || this.#items.length < 2) return;
    this.#clone = this.#items[0].cloneNode(true);
    this.#clone.setAttribute('aria-hidden', 'true');
    this.#clone.removeAttribute('data-shopify-editor-block');
    track.append(this.#clone);
    this.#index = 0;
    track.style.transform = 'translateX(0)';
    this.#setActiveDot();
    this.#resume();
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
    window.clearTimeout(this.#cycleResetTimer);
    this.#cycleResetTimer = undefined;
    const track = this.#track;
    if (this.#clone?.isConnected) this.#clone.remove();
    this.#clone = undefined;
    this.#index = 0;
    if (track) {
      track.style.transition = 'none';
      track.style.transform = 'translateX(0)';
      requestAnimationFrame(() => track.isConnected && track.style.removeProperty('transition'));
    }
    this.#setActiveDot();
  }

  #resume = () => {
    window.clearInterval(this.#timer);
    if (this.#mediaQuery.matches && !this.#reducedMotionQuery.matches && this.dataset.autoplay === 'true') {
      this.#timer = window.setInterval(this.#advance, 3000);
    }
  };

  #pause = () => {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #advance = () => this.#goTo(this.#index + 1);

  #goTo(index) {
    const track = this.#track;
    const count = this.#items.length;
    if (!track || !count) return;
    this.#index = index;
    track.style.transform = `translateX(-${this.#index * 100}%)`;
    this.#setActiveDot();

    // The final slide is a copy of the first one. Reset after the transition
    // as a fallback for browsers or editor updates that do not emit transitionend.
    if (this.#clone && this.#index === count) {
      window.clearTimeout(this.#cycleResetTimer);
      this.#cycleResetTimer = window.setTimeout(this.#returnToFirst, 360);
    }
  }

  #setActiveDot() {
    this.#dots.forEach((dot, index) => dot.setAttribute('aria-selected', String(index === this.#index % Math.max(this.#dots.length, 1))));
  }

  #handleTransitionEnd = (event) => {
    if (event.target !== this.#track || !this.#clone || this.#index !== this.#items.length) return;
    this.#returnToFirst();
  };

  #returnToFirst = () => {
    if (!this.#track || !this.#clone) return;
    window.clearTimeout(this.#cycleResetTimer);
    this.#cycleResetTimer = undefined;
    this.#index = 0;
    this.#track.style.transition = 'none';
    this.#track.style.transform = 'translateX(0)';
    requestAnimationFrame(() => this.#track?.isConnected && this.#track.style.removeProperty('transition'));
    this.#setActiveDot();
  };

  #handleSectionLoad = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#stop();
    this.#buildPagination();
    this.#update();
  };
}

if (!customElements.get('trust-indicators')) {
  customElements.define('trust-indicators', TrustIndicators);
}
